/**
 * What a party lets its members do.
 *
 * A party is smaller and shorter-lived than a crew, so it does not get a role
 * editor - it gets five switches and two ranks. The host and co-hosts are
 * never limited by the switches, because a setting that can lock the host out
 * of their own party is a support ticket rather than a feature.
 *
 * The defaults are exactly what the code did before any of this existed, so a
 * party nobody touches behaves the way it always did.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { Button, Modal, Toggle, useConfirm, useToast } from './ui';
import type { Party } from '@/lib/types';

type Permission = { id: string; name: string; note: string; on: boolean };

export function PartyPermissions({ party, onClose }: { party: Party; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [perms, setPerms] = useState<Permission[]>([]);
  const [values, setValues] = useState<Record<string, boolean>>({});
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let gone = false;
    void api.get<{ permissions: Permission[]; values: Record<string, boolean>; canEdit: boolean }>(
      '/api/parties/' + party.id + '/permissions'
    ).then((r) => {
      if (gone) return;
      setPerms(r.permissions);
      setValues(r.values);
      setCanEdit(r.canEdit);
    }).catch(() => { /* the roster still works without it */ });
    return () => { gone = true; };
  }, [party.id]);

  async function set(id: string, on: boolean) {
    setValues((v) => ({ ...v, [id]: on }));
    setBusy(true);
    try {
      const r = await api.patch<{ values: Record<string, boolean> }>('/api/parties/' + party.id + '/permissions', { [id]: on });
      setValues(r.values);
      void queryClient.invalidateQueries({ queryKey: ['party', party.id] });
    } catch (err) {
      setValues((v) => ({ ...v, [id]: !on }));
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function setRole(userId: string, role: 'officer' | 'member', name: string) {
    if (role === 'member') {
      const ok = await confirm('Step ' + name + ' down?', 'They keep their seat and lose the extra controls.', { confirmLabel: 'Step down' });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.post('/api/parties/' + party.id + '/role', { userId, role });
      void queryClient.invalidateQueries({ queryKey: ['party', party.id] });
      toast(role === 'officer' ? name + ' is a co-host' : name + ' is a member again', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const host = party.members.find((m) => m.userId === party.hostId);
  const others = party.members.filter((m) => m.userId !== party.hostId && m.user);

  return (
    <Modal
      title="Who can do what"
      subtitle="Co-hosts can do everything you can, except hand the party over."
      onClose={onClose}
      wide
    >
      {dialog}

      <section>
        <h4 className="m-0 mb-2 text-[14px] font-bold text-ink">The party</h4>
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {host?.user ? (
            <li className="flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-2.5">
              <Avatar user={host.user} size={32} presence />
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-ink">{host.user.displayName}</span>
              <span className="flex flex-none items-center gap-1.5 rounded-full bg-[color-mix(in_srgb,var(--warn)_18%,transparent)] px-2.5 py-1 text-[11.5px] font-bold text-warn">
                <Icon name="crown" size={12} />
                Host
              </span>
            </li>
          ) : null}

          {others.map((m) => {
            const co = m.role === 'officer';
            return (
              <li key={m.userId} className="flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-2.5">
                <Avatar user={m.user!} size={32} presence />
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-ink">{m.user!.displayName}</span>
                <span className={cn(
                  'flex-none rounded-full px-2.5 py-1 text-[11.5px] font-bold',
                  co ? 'bg-accent-soft text-accent' : 'bg-surface-3 text-mute'
                )}>
                  {co ? 'Co-host' : 'Member'}
                </span>
                {canEdit ? (
                  <Button
                    size="sm"
                    kind="ghost"
                    disabled={busy}
                    onClick={() => void setRole(m.userId, co ? 'member' : 'officer', m.user!.displayName)}
                  >
                    {co ? 'Step down' : 'Make co-host'}
                  </Button>
                ) : null}
              </li>
            );
          })}
          {!others.length ? (
            <li className="rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3 text-center text-[13px] text-mute">
              Nobody else has joined yet.
            </li>
          ) : null}
        </ul>
      </section>

      <section className="mt-4">
        <h4 className="m-0 text-[14px] font-bold text-ink">What members can do</h4>
        <p className="mt-0.5 mb-2 text-[12.5px] text-mute">
          You and your co-hosts can always do all of it.
        </p>
        <div className="flex flex-col gap-1">
          {perms.map((p) => (
            <Toggle
              key={p.id}
              checked={values[p.id] ?? p.on}
              disabled={!canEdit || busy}
              onChange={(v: boolean) => void set(p.id, v)}
              label={p.name}
              note={p.note}
            />
          ))}
          {!perms.length ? (
            <p className="m-0 py-4 text-center text-[13px] text-mute">Loading…</p>
          ) : null}
        </div>
        {!canEdit ? (
          <p className="m-0 mt-2 flex items-center gap-1.5 text-[12px] text-ghost">
            <Icon name="lock" size={12} />
            Only the host can change these.
          </p>
        ) : null}
      </section>
    </Modal>
  );
}
